import type { LinkedAccountPlatform } from "@/utils/linkedaccounts/linkedAccounts";
import { ONBOARDED_FEED_QUEST_ID } from "@constants";
import { VideoUrl } from "./VideoUrl";

export enum DiscoverFeedToCMSType {
  Video = "3889137ad6366322b185a909de3ef69a",
  Quiz = "3497a520053a8e3598eed72bc559527e",
  CTA = "b5a8515fd476d56cff5427b32fac761f",
  Reward = "c35fdac3821694e6678522f351cd9916",
  Landing = "c1844b596b5e984cfbb75eddd8fae3b8",
  PinToHome = "58905c853a991be031025f2e6d3260c3",
  DailyGems = "4f221282392589a3aa92e910f6e6bf4a",
  BonusGems = "4f5f7cfd8513efc0f658ee4e341b57d3",
}

export enum QuizHeaderToCMSType {
  Image = "92ad1b2f6fa1e1bdbf2a7d685469d10e",
  Video = "814cd153ee5bedf9795cb98c8ebf3bb0",
}

export enum ExperimentToCMSType {
  b43814b29b9869cfa8b883e285fee872 = "chessuniverse",
  aa56d3811ead8f6d560419da2c6dc545 = "normainmetaland",
  "23d4ac7124a3bf6826701c42889d7b49" = "generalonboarding",
  "72b23ea60624477e9358b419dfeb7631" = "bitpool",
  "1e78ce238ff46ee43dbd8c7235de33a7" = "selfmadebillionaire",
}

export interface BaseFieldData {
  name: string; // item id
  slug: string; // quest id
  "gems-earnable-2": number;
  priority: number;
  "quest-complete-popup-text"?: string;
  bypass: boolean;
  "onboarding-experience"?: keyof typeof ExperimentToCMSType;
  day0?: boolean;
  categories?: string; // Comma-separated list of categories
  tags?: string; // Comma-separated list of tags
  createdAt?: Date;
}

interface File {
  fileId: string;
  url: string;
  alt: string | null;
}

export interface RewardFieldData extends BaseFieldData {
  "quest-type"?: DiscoverFeedToCMSType.Reward;
  "cta-url"?: string;
  "cta-label"?: string;
  "cta-description"?: string;
  "reward-background": string;
  "reward-signin-redirect-url"?: string;
}

export interface CTAFieldData extends BaseFieldData {
  "quest-type"?: DiscoverFeedToCMSType.CTA;
  "game-id": string;
  "game-name": string;
  "cta-image": File;
  "cta-url"?: string;
  "cta-label"?: string;
  "cta-description"?: string;
  "video-title"?: string; // hack - reuse the video title property for CTA title
}

export interface LandingFieldData extends BaseFieldData {
  "quest-type"?: DiscoverFeedToCMSType.Landing;
  "game-id"?: string;
}

export interface PinToHomeFieldData extends BaseFieldData {
  "quest-type"?: DiscoverFeedToCMSType.PinToHome;
}

export interface DailyGemsFieldData extends BaseFieldData {
  "quest-type"?: DiscoverFeedToCMSType.DailyGems;
}

export interface BonusGemsFieldData extends BaseFieldData {
  "quest-type"?: DiscoverFeedToCMSType.BonusGems;
}

export interface VideoFieldData extends BaseFieldData {
  "game-id": string;
  "game-name": string;
  "quest-type": DiscoverFeedToCMSType.Video;
  "video-title"?: string;
  "cta-url"?: string;
  "cta-label"?: string;
  "video-url"?: string;
}

export interface QuizFieldData extends BaseFieldData {
  "game-id": string;
  "game-name": string;
  "quest-type": DiscoverFeedToCMSType.Quiz;
  "quiz-header-url": string;
  "quiz-header-initial-image"?: File;
  "quiz-header-answered-image"?: File;
  "quiz-answers"?: string;
  "quiz-correct-answers"?: string;
  "quiz-correct-answer-message"?: string;
  "quiz-header-type"?: QuizHeaderToCMSType.Video | QuizHeaderToCMSType.Image;
  "quiz-logo"?: File;
  "quiz-question"?: string;
}

export type DiscoverFeedFieldData =
  | VideoFieldData
  | QuizFieldData
  | CTAFieldData
  | RewardFieldData
  | LandingFieldData
  | PinToHomeFieldData
  | DailyGemsFieldData
  | BonusGemsFieldData;

export type Game = {
  id: string; // e.g. 1234-125151-125-1-51-5 - used for following. TODO: Where do we get this from?
  name: string; // e.g. Dark Machines
};

export type CTA = {
  label?: string;
  description: string; // e.g. "Find out more"
  url: string; // e.g. https://mycoolgame.com
};

export type QuizHeader =
  | {
      url: VideoUrl | undefined;
      type: "video";
    }
  | {
      initialImage: URL | undefined;
      answeredImage: URL | undefined;
      type: "image";
    };

export type FeedQuest = {
  questId: string;
  gemsEarnable: number;
};

export type BaseTemplate = {
  id: string;
  quest: FeedQuest;
  priority: number;
  questCompletePopupText?: string;
  bypass: boolean;
  onboardingExperience?: ExperimentToCMSType;
  day0?: boolean;
  categories: string[];
  tags: string[];
  createdAt?: Date;
};

export type CTATemplate = BaseTemplate & {
  type: "cta";
  image: URL;
  game: Game;
  title: string | undefined;
  description: string | undefined;
  buttonLabel: string | undefined;
  buttonURL: string | undefined;
};

export type LandingTemplate = BaseTemplate & {
  type: "landing";
  gameId?: string;
};

export type RewardTemplate = BaseTemplate & {
  type: "reward";
  rewardBackground: URL | undefined;
  cta: CTA | undefined;
  rewardSigninRedirectUrl: URL | undefined;
};

export type VideoTemplate = BaseTemplate & {
  game: Game;

  // Video specific reqs
  type: "video"; // Discriminator
  title: string | undefined; // e.g. "We think you'll like"
  videoUrl: VideoUrl;
  cta: CTA | undefined;

  quest: FeedQuest;
};

export type QuizTemplate = BaseTemplate & {
  game: Game;

  // Quiz specific reqs
  type: "quiz"; // Discriminator
  logoUrl: URL | undefined;
  header: QuizHeader;
  question: string;
  answers: string[];
  correctAnswers: number[]; // TODO: Does not prevent FE hax to get the right answers. Suggestion: update BE to validate the answers.
  correctAnswerMessage?: string;
};

export type PinToHomeTemplate = BaseTemplate & {
  type: "pintohome";
};

export type DailyGemsTemplate = BaseTemplate & {
  type: "dailygems";
};

export type AllCaughtUpTemplate = BaseTemplate & {
  type: "allcaughtup";
};

export type SocialConnectionTemplate = BaseTemplate & {
  type: "socialconnection";
  platform: LinkedAccountPlatform;
};

export type BonusGemsTemplate = BaseTemplate & {
  type: "bonusgems";
};

export type DiscoverFeedItem =
  | VideoTemplate
  | QuizTemplate
  | CTATemplate
  | RewardTemplate
  | LandingTemplate
  | PinToHomeTemplate
  | DailyGemsTemplate
  | AllCaughtUpTemplate
  | SocialConnectionTemplate
  | BonusGemsTemplate
  | undefined;
export type DiscoverFeed = DiscoverFeedItem[];

const toBaseTemplate = (fieldData: BaseFieldData): BaseTemplate => {
  return {
    id: fieldData.slug,
    questCompletePopupText: fieldData["quest-complete-popup-text"],
    quest: {
      questId: fieldData.slug,
      gemsEarnable: fieldData["gems-earnable-2"],
    },
    bypass: fieldData.bypass,
    day0: fieldData.day0 ?? false,
    onboardingExperience: fieldData["onboarding-experience"]
      ? ExperimentToCMSType[fieldData["onboarding-experience"]]
      : undefined,
    priority: fieldData.priority,
    categories: fieldData.categories
      ? fieldData.categories.split(",").map((cat) => cat.trim().toLowerCase())
      : [],
    tags: fieldData.tags
      ? fieldData.tags.split(",").map((tag) => tag.trim().toLowerCase())
      : [],
    createdAt: fieldData.createdAt,
  };
};

const toRewardTemplate = (
  fieldData: RewardFieldData,
): RewardTemplate | undefined => {
  if (!fieldData["reward-background"]) {
    console.warn("Reward template is missing required fields", fieldData);
    return undefined;
  }

  return {
    ...toBaseTemplate(fieldData),
    type: "reward",
    quest: {
      questId: `${ONBOARDED_FEED_QUEST_ID}-event`, // Expected id to make sure the user has completed an onboarding experiment
      gemsEarnable: fieldData["gems-earnable-2"],
    },
    rewardBackground: new URL(fieldData["reward-background"]),
    cta:
      !fieldData["cta-url"] || !fieldData["cta-label"]
        ? undefined
        : {
            label: fieldData["cta-label"],
            description: fieldData["cta-description"] || "",
            url: fieldData["cta-url"],
          },
    rewardSigninRedirectUrl: fieldData["reward-signin-redirect-url"]
      ? new URL(fieldData["reward-signin-redirect-url"])
      : undefined,
  };
};

const toCTATemplate = (fieldData: CTAFieldData): CTATemplate | undefined => {
  if (!fieldData["cta-image"]) {
    console.warn("CTA template is missing required fields", fieldData);
    return undefined;
  }

  return {
    ...toBaseTemplate(fieldData),
    type: "cta",
    game: {
      id: fieldData["game-id"],
      name: fieldData["game-name"],
    },
    image: new URL(fieldData["cta-image"].url),
    title: fieldData["video-title"],
    description: fieldData["cta-description"],
    buttonLabel: fieldData["cta-label"],
    buttonURL: fieldData["cta-url"],
  };
};

const toVideoTemplate = (
  fieldData: VideoFieldData,
): VideoTemplate | undefined => {
  if (!fieldData["video-url"]) {
    console.warn("Video template is missing required fields", fieldData);
    return undefined;
  }

  let constructedVideoUrl: VideoUrl | undefined;
  try {
    constructedVideoUrl = new VideoUrl(fieldData["video-url"]);
  } catch (e) {
    console.error("Error constructing VideoUrl:", e);
    return undefined;
  }

  return {
    ...toBaseTemplate(fieldData),
    game: {
      id: fieldData["game-id"],
      name: fieldData["game-name"],
    },
    type: "video",
    title: fieldData["video-title"],
    videoUrl: new VideoUrl(fieldData["video-url"]),
    cta:
      !fieldData["cta-url"] || !fieldData["cta-label"]
        ? undefined
        : {
            description: fieldData["cta-label"],
            url: fieldData["cta-url"],
          },
  };
};

const toQuizTemplate = (fieldData: QuizFieldData): QuizTemplate | undefined => {
  // TODO: Better type safety for fields
  if (
    !fieldData["quiz-question"] ||
    !fieldData["quiz-answers"] ||
    !fieldData["quiz-correct-answers"] ||
    fieldData["quiz-answers"].split(", ").length === 0 ||
    fieldData["quiz-correct-answers"].split(", ").length === 0
  ) {
    console.warn("Quiz template is missing required fields", fieldData);
    return undefined;
  }

  return {
    ...toBaseTemplate(fieldData),
    game: {
      id: fieldData["game-id"],
      name: fieldData["game-name"],
    },
    type: "quiz",
    logoUrl: fieldData["quiz-logo"]
      ? new URL(fieldData["quiz-logo"].url)
      : undefined,
    header:
      fieldData["quiz-header-type"] === QuizHeaderToCMSType.Video
        ? {
            type: "video",
            url: new VideoUrl(fieldData["quiz-header-url"]),
          }
        : {
            type: "image",
            initialImage: fieldData["quiz-header-initial-image"]
              ? new URL(fieldData["quiz-header-initial-image"].url)
              : undefined,
            answeredImage: fieldData["quiz-header-answered-image"]
              ? new URL(fieldData["quiz-header-answered-image"].url)
              : undefined,
          },
    question: fieldData["quiz-question"],
    answers: fieldData["quiz-answers"].split(", ").map((a) => a.trim()),
    correctAnswers: fieldData["quiz-correct-answers"]
      ? fieldData["quiz-correct-answers"]
          .split(", ")
          .map((a) => Number.parseInt(a.trim()))
      : [],
    correctAnswerMessage: fieldData["quiz-correct-answer-message"],
  };
};

const toLandingTemplate = (
  fieldData: LandingFieldData,
): LandingTemplate | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "landing",
    gameId: fieldData["game-id"],
  };
};

const toPinToHomeTemplate = (
  fieldData: PinToHomeFieldData,
): PinToHomeTemplate | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "pintohome",
  };
};

const toDailyGemsTemplate = (
  fieldData: DailyGemsFieldData,
): DailyGemsTemplate | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "dailygems",
  };
};

const toBonusGemsTemplate = (
  fieldData: BonusGemsFieldData,
): BonusGemsTemplate | undefined => {
  return {
    ...toBaseTemplate(fieldData),
    type: "bonusgems",
  };
};

export const getDiscoverFeedItemFromFieldData = (
  fieldData: DiscoverFeedFieldData,
): DiscoverFeedItem => {
  switch (fieldData["quest-type"]) {
    case DiscoverFeedToCMSType.Video:
      return toVideoTemplate(fieldData);
    case DiscoverFeedToCMSType.Quiz:
      return toQuizTemplate(fieldData);
    case DiscoverFeedToCMSType.CTA:
      return toCTATemplate(fieldData);
    case DiscoverFeedToCMSType.Reward:
      return toRewardTemplate(fieldData);
    case DiscoverFeedToCMSType.Landing:
      return toLandingTemplate(fieldData);
    case DiscoverFeedToCMSType.PinToHome:
      return toPinToHomeTemplate(fieldData);
    case DiscoverFeedToCMSType.DailyGems:
      return toDailyGemsTemplate(fieldData);
    case DiscoverFeedToCMSType.BonusGems:
      return toBonusGemsTemplate(fieldData);
    default:
      return undefined;
  }
};
